import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  createDirectChat,
  getUserByEmail,
  toggleLike,
  getLikesSnap,
} from "../../../utils/firebase/Firebase.utils";
import { FiMessageSquare } from "react-icons/fi";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import PhotoCarousel from "./PhotoCarousel";
import ProductDetailsModal from "./ProductDetailsModal";

const ProductCard = ({ product }) => {
  const currentUser = useSelector((state) => state.userObject);
  const navigate = useNavigate();
  const [sellerId, setSellerId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    // Fetch seller's user ID if we have their email
    const fetchSellerId = async () => {
      if (product.createdBy && !product.createdBy.includes("_")) {
        // If it's an email (doesn't contain underscore like UIDs do)
        try {
          const sellerData = await getUserByEmail("users", product.createdBy);
          if (sellerData) {
            setSellerId(sellerData.id);
          }
        } catch (error) {
          console.error("Error fetching seller ID:", error);
        }
      } else {
        setSellerId(product.createdBy); // If it's already a UID
      }
    };

    fetchSellerId();
  }, [product.createdBy]);

  useEffect(() => {
    if (!currentUser) return;

    // Subscribe to likes updates
    const unsubscribe = getLikesSnap(product.id, "product", (likesData) => {
      setLiked(likesData.likes.includes(currentUser.uid));
      setLikeCount(likesData.count || 0);
    });

    return () => unsubscribe();
  }, [product.id, currentUser]);

  const handleContactSeller = async (e) => {
    if (e) {
      e.stopPropagation(); // Prevent card click event
    }

    if (!currentUser) {
      navigate("/login");
      return;
    }

    if (!sellerId) {
      console.error("Seller ID not found");
      return;
    }

    try {
      // Create or get existing chat
      const chatId = await createDirectChat(currentUser.uid, sellerId, {
        productId: product.id,
        productTitle: product.productTitle,
        productImage: product.images?.[0] || null,
        price: product.price,
      });

      // Navigate to chat
      navigate(`/chats/${chatId}`);
    } catch (error) {
      console.error("Error creating chat:", error);
    }
  };

  const handleToggleLike = async (e) => {
    e.stopPropagation();

    if (!currentUser) {
      navigate("/login");
      return;
    }

    try {
      const isLiked = await toggleLike(product.id, currentUser.uid, "product");
      setLiked(isLiked);
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  };

  return (
    <>
      <div
        className="relative p-2 bg-white rounded-lg shadow-md mt-2 hover:shadow-lg transition-all duration-300 w-[350px] self-center cursor-pointer"
        onClick={() => setShowModal(true)}
      >
        <div className="overflow-hidden rounded-lg">
          {product.images && product.images.length > 0 ? (
            <PhotoCarousel images={product.images} />
          ) : (
            <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
              <span className="text-gray-400 text-xs sm:text-sm">No Image</span>
            </div>
          )}
        </div>

        {product.productCategories && product.productCategories[0] && (
          <div className="bg-[#8FCD00] text-sm font-bold rounded-lg w-fit px-2 py-1 absolute top-4 right-4">
            {product.productCategories[0]}
          </div>
        )}

        <div className="p-3">
          <div className="font-bold text-[#363062]">₱{product.price}</div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[#363062] text-sm truncate pr-2">
              {product.productTitle}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{likeCount}</span>
              <button onClick={handleToggleLike}>
                <div className="text-2xl">
                  {liked ? (
                    <FaHeart className="text-red-500" />
                  ) : (
                    <FaRegHeart />
                  )}
                </div>
              </button>
            </div>
          </div>

          <button
            onClick={handleContactSeller}
            disabled={!sellerId}
            className="w-full bg-[#363062] hover:bg-[#363062]/90 text-white py-2 px-4 rounded-md transition-colors duration-300 text-sm flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiMessageSquare className="w-4 h-4" />
            Contact Seller
          </button>

          <div
            className="underline cursor-pointer text-xs text-gray-400 mt-2 text-center"
            onClick={(e) => {
              e.stopPropagation();
              setShowModal(true);
            }}
          >
            View Details
          </div>
        </div>
      </div>

      {showModal && (
        <ProductDetailsModal
          product={product}
          onClose={() => setShowModal(false)}
          onContactSeller={handleContactSeller}
          liked={liked}
          onToggleLike={handleToggleLike}
          likeCount={likeCount}
        />
      )}
    </>
  );
};

export default ProductCard;